import jsPDF from "jspdf";
import { ArchiveDescription } from "../../api/archive";

// Customer-facing PDF of the archive description document. Branding +
// page chrome intentionally mirror exportDetail/manifestRender.ts (the
// export manifest PDF); kept standalone rather than sharing a helper so
// a change to one report can't silently reflow the other. If a third
// report shows up, factor these into a shared pdf util.

const PAGE_W = 612;
const MARGIN_X = 54;
const BODY_TOP = 110;
const BODY_BOTTOM = 742;
const FOOTER_Y = 762;
const CONTENT_W = PAGE_W - 2 * MARGIN_X;

const COLOR_PRIMARY: readonly [number, number, number] = [40, 109, 178];
const COLOR_SECONDARY: readonly [number, number, number] = [120, 168, 66];
const COLOR_INK: readonly [number, number, number] = [4, 36, 65];
const COLOR_MUTED: readonly [number, number, number] = [120, 130, 145];
const COLOR_RULE: readonly [number, number, number] = [225, 230, 238];

interface PdfState {
  doc: jsPDF;
  y: number;
  page: number;
}

type RgbSetter = (r: number, g: number, b: number) => unknown;

function applyRgb(setter: RgbSetter, c: readonly [number, number, number]): void {
  setter(c[0], c[1], c[2]);
}

const fmt = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : Math.round(n).toLocaleString();

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function pageChrome(state: PdfState): void {
  const { doc } = state;
  applyRgb(doc.setFillColor.bind(doc), COLOR_PRIMARY);
  doc.rect(0, 0, PAGE_W, 6, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.text("TVEyes • Archive Description", MARGIN_X, 36);
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.line(MARGIN_X, 44, PAGE_W - MARGIN_X, 44);
  doc.text(`Page ${state.page}`, PAGE_W - MARGIN_X, FOOTER_Y, { align: "right" });
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
}

function forcePageBreak(state: PdfState): void {
  state.doc.addPage();
  state.page += 1;
  pageChrome(state);
  state.y = BODY_TOP;
}

function ensureSpace(state: PdfState, needed: number): void {
  if (state.y + needed > BODY_BOTTOM) forcePageBreak(state);
}

function section(state: PdfState, title: string): void {
  ensureSpace(state, 60);
  const { doc } = state;
  state.y += 8;
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, MARGIN_X, state.y);
  state.y += 8;
  applyRgb(doc.setFillColor.bind(doc), COLOR_SECONDARY);
  doc.rect(MARGIN_X, state.y, 36, 2, "F");
  state.y += 18;
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
}

interface Col {
  label: string;
  width: number;
  align?: "right";
}

function drawTable(state: PdfState, cols: Col[], rows: string[][]): void {
  const { doc } = state;
  const header = () => {
    applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    let x = MARGIN_X;
    for (const c of cols) {
      const right = c.align === "right";
      doc.text(c.label.toUpperCase(), right ? x + c.width - 4 : x, state.y, {
        align: right ? "right" : "left",
      });
      x += c.width;
    }
    state.y += 5;
    applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
    doc.line(MARGIN_X, state.y, PAGE_W - MARGIN_X, state.y);
    state.y += 13;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  };
  header();
  for (const row of rows) {
    if (state.y + 14 > BODY_BOTTOM) {
      forcePageBreak(state);
      header();
    }
    let x = MARGIN_X;
    for (let i = 0; i < cols.length; i++) {
      const c = cols[i];
      const right = c.align === "right";
      doc.text(row[i] ?? "", right ? x + c.width - 4 : x, state.y, {
        align: right ? "right" : "left",
      });
      x += c.width;
    }
    state.y += 14;
  }
  state.y += 6;
}

function drawParagraphs(state: PdfState, lines: string[]): void {
  const { doc } = state;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  for (const line of lines) {
    const wrapped = doc.splitTextToSize(line, CONTENT_W) as string[];
    for (const w of wrapped) {
      ensureSpace(state, 14);
      doc.text(w, MARGIN_X, state.y);
      state.y += 14;
    }
  }
}

// Render and trigger a browser download of the archive description PDF.
export function renderArchiveReport(d: ArchiveDescription): void {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const state: PdfState = { doc, y: BODY_TOP, page: 1 };
  pageChrome(state);

  // Cover hero
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.text("Archive Description", MARGIN_X, 70);
  applyRgb(doc.setFillColor.bind(doc), COLOR_SECONDARY);
  doc.rect(MARGIN_X, 78, 48, 3, "F");
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const span =
    d.coverage.first_month && d.coverage.last_month
      ? `${d.coverage.first_month} → ${d.coverage.last_month}`
      : "coverage span unavailable";
  doc.text(
    `${d.content_type} · ${span} · ${fmt(d.months_covered)} months described`,
    MARGIN_X,
    98,
  );
  state.y = 130;

  // Headline totals as key/value rows.
  const kv: [string, string][] = [
    ["Pages", fmt(d.totals.pages)],
    ["Superdocuments", fmt(d.totals.superdocuments)],
    ["Broadcast hours", fmt(d.totals.duration_hours)],
    ["Distinct stations", fmt(d.totals.distinct_stations)],
    ["Stations arrived", fmt(d.auto_remarks.arrivals.length)],
    ["Stations departed", fmt(d.auto_remarks.departures.length)],
    ["Stations re-keyed", fmt(d.auto_remarks.rekeys.length)],
    ["Station outages", fmt(d.auto_remarks.outages.length)],
  ];
  doc.setFontSize(10);
  for (const [label, value] of kv) {
    ensureSpace(state, 16);
    applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
    doc.setFont("helvetica", "normal");
    doc.text(label, MARGIN_X, state.y);
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    doc.setFont("helvetica", "bold");
    doc.text(value, MARGIN_X + 160, state.y);
    state.y += 16;
  }

  // Monthly coverage
  section(state, "Monthly coverage");
  drawTable(
    state,
    [
      { label: "Month", width: 74 },
      { label: "Pages", width: 90, align: "right" },
      { label: "Hours", width: 70, align: "right" },
      { label: "Stations", width: 70, align: "right" },
      { label: "Days", width: 80, align: "right" },
      { label: "Missing", width: 70, align: "right" },
    ],
    d.timeline.map((t) => [
      t.month,
      fmt(t.pages),
      fmt(t.duration_hours),
      fmt(t.distinct_stations),
      `${t.days_present ?? "—"}/${t.days_in_month ?? "—"}`,
      t.missing_days > 0 ? String(t.missing_days) : "—",
    ]),
  );

  // Networks
  section(state, `Networks${d.by_network_truncated ? ` (top ${d.by_network.length})` : ""}`);
  drawTable(
    state,
    [
      { label: "Station", width: 214 },
      { label: "Pages", width: 90, align: "right" },
      { label: "Months", width: 60, align: "right" },
      { label: "First", width: 70 },
      { label: "Last", width: 60 },
    ],
    d.by_network.map((n) => [
      truncate(n.source_name ?? n.source_id, 40),
      fmt(n.pages),
      fmt(n.months_present),
      n.first_month,
      n.last_month,
    ]),
  );

  // Categories
  section(state, "Categories");
  drawTable(
    state,
    [
      { label: "Category", width: 284 },
      { label: "Pages", width: 110, align: "right" },
      { label: "Hours", width: 110, align: "right" },
    ],
    d.by_category.map((c) => [
      truncate(c.category ?? "uncategorized", 52),
      fmt(c.pages),
      fmt(c.duration_hours),
    ]),
  );

  // Errata (human notes)
  section(state, "Errata");
  if (d.errata.length === 0) {
    drawParagraphs(state, ["No errata recorded for this archive."]);
  } else {
    for (const e of d.errata) {
      for (const note of e.notes) {
        drawParagraphs(state, [`${e.year}-${e.month}: ${note.text}`]);
      }
    }
  }

  doc.save(`archive-description-${d.content_type}.pdf`);
}
